import { countBy, groupBy, isNil, pick } from "lodash";
import pluralize from "pluralize";
import { type FieldErrors } from "react-hook-form";

import {
  isAssetFormationProperty,
  isBooleanFormationProperty,
  isCampaignFormationProperty,
  isDropdownFormationProperty,
  isPlaylistFormationProperty,
} from "@ds/modules/settings/projects/utils/formation-guards";
import { getDeviceScopedKeys } from "@ds/modules/settings/projects/utils/formation-roles";
import {
  isRequiredFormationPropertyValidationRule,
  isUniqueFormationPropertyValidationRule,
} from "@ds/modules/settings/projects/utils/formation-rule-guards";

export type PlayerRoleIssue = {
  /** Errors block saving, warnings only leave the experience incomplete */
  severity: "error" | "warning";
  message: string;
  /** The player's `role`, or the key of the role field the issue is about */
  field: string;
};

export type PlayerRolesContext = {
  roles: FormationRoleMap;
  properties: TemplateFormationFieldPropertyMap;
  devices?: ExperienceDeviceFormationMap;
  playerIds: number[];
};

const isEmptyValue = (value: unknown) => isNil(value) || value === "";

const getPropertyTitle = (properties: TemplateFormationFieldPropertyMap, field: string) =>
  properties[field]?.title || field;

export const getRoleTitle = (roles: FormationRoleMap, roleKey: string) => roles[roleKey]?.title || roleKey;

export const getPlayerRole = (devices: ExperienceDeviceFormationMap | undefined, playerId: number) =>
  devices?.[playerId]?.role || null;

/** How many of the given players have each role */
export const getRoleCounts = (devices: ExperienceDeviceFormationMap | undefined, playerIds: number[]) =>
  countBy(
    playerIds.map(playerId => getPlayerRole(devices, playerId)).filter((role): role is string => !!role),
  ) as Record<string, number>;

/**
 * Problems with each player's role assignment, keyed by player ID. Mirrors the server: a role over its `max` or a
 * duplicate `unique` value rejects the save, while a missing role or required value only leaves the experience
 * incomplete.
 */
export const getPlayerRoleIssues = ({
  roles,
  properties,
  devices,
  playerIds,
}: PlayerRolesContext): Record<number, PlayerRoleIssue[]> => {
  const issues: Record<number, PlayerRoleIssue[]> = {};
  const addIssue = (playerId: number, issue: PlayerRoleIssue) => {
    issues[playerId] = [...(issues[playerId] || []), issue];
  };

  const roleCounts = getRoleCounts(devices, playerIds);
  playerIds.forEach(playerId => {
    const roleKey = getPlayerRole(devices, playerId);
    if (!roleKey) {
      addIssue(playerId, { severity: "warning", message: "No role", field: "role" });
      return;
    }

    const role = roles[roleKey];
    if (!role) {
      addIssue(playerId, { severity: "error", message: `Unknown role ${roleKey}`, field: "role" });
      return;
    }

    if (!isNil(role.max) && (roleCounts[roleKey] || 0) > role.max) {
      addIssue(playerId, {
        severity: "error",
        message: `Only ${pluralize("player", role.max, true)} can be ${role.title}`,
        field: "role",
      });
    }

    role.fields.forEach(field => {
      const isRequired = (properties[field]?.metadata?.validation_rules || []).some(
        rule => isRequiredFormationPropertyValidationRule(rule) && rule.value,
      );

      if (isRequired && isEmptyValue(devices?.[playerId]?.properties?.[field]?.value)) {
        addIssue(playerId, { severity: "warning", message: `${getPropertyTitle(properties, field)} missing`, field });
      }
    });
  });

  getDeviceScopedKeys(roles).forEach(field => {
    const uniqueRule = (properties[field]?.metadata?.validation_rules || []).find(
      isUniqueFormationPropertyValidationRule,
    );
    if (!uniqueRule?.value) {
      return;
    }

    const playersWithValue = playerIds.filter(playerId => {
      const roleKey = getPlayerRole(devices, playerId);
      return (
        !!roleKey &&
        !!roles[roleKey]?.fields.includes(field) &&
        !isEmptyValue(devices?.[playerId]?.properties?.[field]?.value)
      );
    });

    Object.values(groupBy(playersWithValue, playerId => String(devices?.[playerId]?.properties?.[field]?.value)))
      .filter(playersSharingValue => playersSharingValue.length > 1)
      .flat()
      .forEach(playerId =>
        addIssue(playerId, {
          severity: "error",
          message: uniqueRule.error || `${getPropertyTitle(properties, field)} is used by another player`,
          field,
        }),
      );
  });

  return issues;
};

/** IDs of the players whose role or values have form errors */
export const getPlayerIdsWithErrors = (errors: FieldErrors<ExperienceFormData>) =>
  Object.keys((errors.experience?.formation as FieldErrors<EntityFormation> | undefined)?.devices || {}).map(Number);

/** Roles that have fewer players than their `min` */
export const getRoleShortfalls = ({ roles, devices, playerIds }: Omit<PlayerRolesContext, "properties">) => {
  const roleCounts = getRoleCounts(devices, playerIds);

  return Object.entries(roles)
    .filter(([roleKey, role]) => !isNil(role.min) && (roleCounts[roleKey] || 0) < role.min)
    .map(([, role]) => `${role.title} role needs at least ${pluralize("player", role.min, true)}`);
};

/** Role choices for a player; a role already filled up by other players can't be picked */
export const getRoleOptions = (
  roles: FormationRoleMap,
  devices: ExperienceDeviceFormationMap | undefined,
  playerIds: number[],
  playerId: number,
) => {
  const otherPlayersRoleCounts = getRoleCounts(
    devices,
    playerIds.filter(id => id !== playerId),
  );

  const currentRole = getPlayerRole(devices, playerId);

  return Object.entries(roles).map(([roleKey, role]) => {
    // the player's own role stays selectable, even when the role is over its max
    const isFull = roleKey !== currentRole && !isNil(role.max) && (otherPlayersRoleCounts[roleKey] || 0) >= role.max;

    return {
      value: roleKey,
      label: isFull ? `${role.title} (full)` : role.title,
      disabled: isFull,
    };
  });
};

/** A player's assignment after switching to another role: values of fields the new role doesn't have are dropped */
export const switchPlayerRole = (
  roles: FormationRoleMap,
  deviceFormation: ExperienceDeviceFormation | undefined,
  roleKey: string | null,
): ExperienceDeviceFormation => ({
  role: roleKey,
  properties: pick(deviceFormation?.properties || {}, roleKey ? roles[roleKey]?.fields || [] : []),
});

/**
 * A player's value of a field as text, or null when it's empty or refers to another entity (a playlist, asset or
 * campaign), which is shown by its own component
 */
export const formatPlayerValue = (property: TemplateFormationProperty, value: unknown) => {
  if (isEmptyValue(value)) {
    return null;
  }

  if (
    isPlaylistFormationProperty(property) ||
    isAssetFormationProperty(property) ||
    isCampaignFormationProperty(property)
  ) {
    return null;
  }

  if (isBooleanFormationProperty(property)) {
    return value ? "Yes" : "No";
  }

  if (isDropdownFormationProperty(property)) {
    return property.metadata?.options?.find(option => option.value === value)?.label || String(value);
  }

  return String(value);
};

const getSummaryValue = (property: TemplateFormationProperty, value: unknown) => {
  const formattedValue = formatPlayerValue(property, value);

  // a bare Yes/No means nothing in a one-line summary without its field
  return formattedValue && isBooleanFormationProperty(property)
    ? `${property.title}: ${formattedValue}`
    : formattedValue;
};

/** A one-line summary of a player's role values, e.g. "Slot 1 · QN65QN90FAFXZA" */
export const getPlayerRoleSummary = (
  roles: FormationRoleMap,
  properties: TemplateFormationFieldPropertyMap,
  deviceFormation: ExperienceDeviceFormation | undefined,
) => {
  const role = deviceFormation?.role ? roles[deviceFormation.role] : undefined;

  return (role?.fields || [])
    .map(field =>
      properties[field] ? getSummaryValue(properties[field], deviceFormation?.properties?.[field]?.value) : null,
    )
    .filter(value => !!value)
    .join(" · ");
};
